import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { Visibility as ViewIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { assessmentResultsAPI, assessmentsAPI } from '../services/api'
import { usePatientFilter } from '../hooks/usePatientFilter'

// Severity styles
const severityStyles = {
  none: { color: '#71717a', label: 'None' },
  minimal: { color: '#16a34a', label: 'Minimal' },
  mild: { color: '#0284c7', label: 'Mild' },
  moderate: { color: '#ca8a04', label: 'Moderate' },
  moderately_severe: { color: '#ea580c', label: 'Mod. Severe' },
  severe: { color: '#dc2626', label: 'Severe' },
}

function SeverityIndicator({ severity }) {
  if (!severity) return <Typography variant="body2" color="text.secondary">N/A</Typography>
  const style = severityStyles[severity] || { color: '#71717a', label: severity.replace('_', ' ') }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

const columns = [
  { field: 'id', headerName: 'ID', width: 60 },
  { field: 'user_name', headerName: 'Patient', width: 180 },
  {
    field: 'instrument_code',
    headerName: 'Code',
    width: 90,
  },
  {
    field: 'instrument_name',
    headerName: 'Assessment',
    width: 180,
  },
  {
    field: 'submitted_date',
    headerName: 'Submitted',
    width: 140,
    valueGetter: (params) => new Date(params.value).toLocaleDateString()
  },
  {
    field: 'calculated_score',
    headerName: 'Score',
    width: 70,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (params) => params.value ?? 'N/A'
  },
  {
    field: 'severity',
    headerName: 'Severity',
    width: 110,
    renderCell: (params) => <SeverityIndicator severity={params.value} />
  },
]

function AssessmentResults() {
  const [assessmentResults, setAssessmentResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openViewDialog, setOpenViewDialog] = useState(false)
  const [selectedResult, setSelectedResult] = useState(null)
  const [assessments, setAssessments] = useState([])
  const [filterAssessment, setFilterAssessment] = useState('')

  const { getSelectedPatientId, hasSelectedPatient } = usePatientFilter()

  useEffect(() => {
    fetchAssessments()
  }, [])

  useEffect(() => {
    const selectedPatientId = getSelectedPatientId()
    if (selectedPatientId) {
      fetchAssessmentResultsForPatient(selectedPatientId)
    } else {
      setAssessmentResults([])
      setLoading(false)
    }
  }, [getSelectedPatientId()])

  const fetchAssessmentResultsForPatient = async (userId) => {
    try {
      setLoading(true)
      setError(null)
      const response = await assessmentResultsAPI.getAssessmentResultsByUserId(userId)
      setAssessmentResults(response.data.result || response.data)
    } catch (err) {
      console.error('Error fetching assessment results:', err)
      setError('Failed to load assessment results. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const fetchAssessments = async () => {
    try {
      const response = await assessmentsAPI.getAssessments()
      setAssessments(response.data)
    } catch (err) {
      console.error('Error fetching assessments:', err)
    }
  }


  const handleViewResult = (result) => {
    setSelectedResult(result)
    setOpenViewDialog(true)
  }

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false)
    setSelectedResult(null)
  }

  const handleDelete = async (resultId) => {
    if (window.confirm('Are you sure you want to delete this assessment result?')) {
      try {
        await assessmentResultsAPI.deleteAssessmentResult(resultId)
        const selectedPatientId = getSelectedPatientId()
        if (selectedPatientId) {
          fetchAssessmentResultsForPatient(selectedPatientId)
        }
      } catch (err) {
        console.error('Error deleting assessment result:', err)
        setError('Failed to delete assessment result. Please try again.')
      }
    }
  }

  const formatResults = (results) => {
    if (!results) return 'No results available'

    try {
      const resultObj = typeof results === 'string' ? JSON.parse(results) : results
      const responses = resultObj.responses || resultObj

      return (
        <div>
          {Object.entries(responses).map(([questionId, answer]) => (
            <div key={questionId} style={{ marginBottom: '8px' }}>
              <strong>Question {questionId}:</strong> {answer}
            </div>
          ))}
        </div>
      )
    } catch (e) {
      return 'Invalid result format'
    }
  }

  const getFilteredResults = () => {
    let filtered = assessmentResults

    if (filterAssessment) {
      filtered = filtered.filter(result => result.instrument_code === filterAssessment)
    }

    return filtered
  }

  const actionColumn = {
    field: 'actions',
    headerName: '',
    width: 80,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" gap={0.5}>
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => handleViewResult(params.row)}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => handleDelete(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }

  const allColumns = [...columns, actionColumn]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (!hasSelectedPatient()) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Assessment Results</Typography>
        <Alert severity="warning">
          Please select a patient from the dropdown above to view their assessment results.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Assessment Results</Typography>
          <Typography variant="body2" color="text.secondary">
            View completed patient assessments and scores.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Assessment Filter */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Assessment</InputLabel>
            <Select
              value={filterAssessment}
              label="Assessment"
              onChange={(e) => setFilterAssessment(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {assessments.map((assessment) => (
                <MenuItem key={assessment.id} value={assessment.code}>
                  {assessment.code} - {assessment.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {getFilteredResults().length} results
          </Typography>
        </Box>
      </Paper>

      <Paper>
        <DataGrid
          rows={getFilteredResults()}
          columns={allColumns}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50]}
          autoHeight
          disableSelectionOnClick
          loading={loading}
        />
      </Paper>

      {/* View Result Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Assessment Result Details</DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Patient
                    </Typography>
                    <Typography variant="body2">
                      {selectedResult.user_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Assessment
                    </Typography>
                    <Typography variant="body2">
                      {selectedResult.instrument_code} - {selectedResult.instrument_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Date Submitted
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedResult.submitted_date).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Score
                    </Typography>
                    <Typography variant="body2">
                      {selectedResult.calculated_score ?? 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedResult.severity && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">
                        Severity
                      </Typography>
                      <Box>
                        <SeverityIndicator severity={selectedResult.severity} />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Responses
                </Typography>
                <Box>
                  {formatResults(selectedResult.results)}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AssessmentResults